<template>
  <div class="max-w-5xl mx-auto px-4 sm:px-6 relative min-h-[80vh] flex flex-col justify-center pt-24 sm:pt-0">
    
    <div class="absolute top-4 right-4 sm:top-6 sm:right-6 z-20">
      <div class="relative group flex items-center gap-2 bg-white border border-gray-200 shadow-sm rounded-full px-3 py-1.5 transition-all hover:shadow-md hover:border-gray-300">
        
        <img :src="flagUrl" class="w-5 h-auto rounded-[2px] object-cover opacity-90" alt="flag" />
        
        <select
          v-model="selectedCountry"
          class="appearance-none bg-transparent font-bold text-sm text-gray-700 focus:outline-none cursor-pointer pr-6 py-0.5 z-10"
        >
          <option value="US">USD</option>
          <option value="Canada">CAD</option>
        </select>

        <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 group-hover:text-gray-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <div class="flex flex-col justify-center items-center gap-6 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      <div class="space-y-4">
        <span class="block text-2xl sm:text-4xl font-bold text-gray-900">What if you invested</span>
        
        <div class="relative inline-block group">
          <span class="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400 font-light group-focus-within:text-green-500 transition-colors">$</span>
          <input
            type="text"
            inputmode="numeric"
            :value="displayValue"
            @input="handleInput"
            class="block w-full sm:w-64 bg-white border-2 border-gray-200 rounded-2xl py-4 pl-10 pr-4 text-center text-3xl sm:text-4xl font-bold text-gray-900 focus:outline-none focus:border-green-500 focus:ring-4 focus:ring-green-500/10 transition-all shadow-sm placeholder:text-gray-300"
            placeholder="10,000"
          />
        </div>
        
        <span class="block text-2xl sm:text-4xl font-bold text-gray-900">at the start of {{ currentYear }}?</span>
      </div>

      <p class="text-gray-500 text-lg mt-2 max-w-lg mx-auto">
        See how your money would have grown across different risk profiles.
      </p>

    </div>

    <div class="mt-12 sm:mt-16">
      
      <div v-if="isLoading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 px-2 sm:px-0">
        <div
          v-for="item in computedResults"
          :key="item.risk"
          class="bg-white rounded-2xl p-5 text-center shadow-sm border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          :class="tileBorderClass(item.risk)"
        >
          <div class="text-3xl mb-2">{{ getEmoji(item.risk) }}</div>
          <h3 class="text-xs uppercase font-bold tracking-wider text-gray-400 mb-1">{{ item.risk }}</h3>
          <p class="text-2xl font-extrabold text-gray-900 tracking-tight">${{ formatNumber(item.finalValue) }}</p>
          <div class="mt-2 inline-block px-2 py-0.5 rounded text-xs font-bold" :class="getReturnColorBadge(item.returnPct)">
             {{ item.returnPct > 0 ? '+' : '' }}{{ item.returnPct }}%
          </div>
        </div>
      </div>

    </div>

    <div class="text-center mt-12 sm:mt-16">
      <router-link
        to="/select-risk"
        class="group inline-flex items-center gap-2 bg-gray-900 text-white font-bold px-8 py-4 rounded-xl shadow-xl hover:bg-black hover:scale-105 transition-all duration-200"
      >
        <span>Run Your Numbers</span>
        <svg class="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
      </router-link>
      <p class="text-xs text-gray-400 mt-4">No login required.</p>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import { detectUserCountry } from "@/utils/location";

export default {
  name: "HomePage",
  data() {
    return {
      rawValue: 10000, 
      selectedCountry: "US",
      multipliers: {}, 
      isLoading: false,
      currentYear: new Date().getFullYear()
    };
  },
  computed: {
    displayValue() {
      return this.rawValue ? this.rawValue.toLocaleString() : "";
    },
    flagUrl() {
      return this.selectedCountry === "Canada"
        ? "https://flagcdn.com/ca.svg"
        : "https://flagcdn.com/us.svg";
    },
    computedResults() {
      const order = ["Risk-Free", "Conservative", "Balanced", "Aggressive"];
      return order.map((risk) => {
        const multiplier = this.multipliers[risk] || 1.0;
        const finalValue = this.rawValue * multiplier;
        const returnPct = ((multiplier - 1) * 100).toFixed(1);
        
        return {
          risk,
          finalValue,
          returnPct
        };
      });
    },
  },
  methods: {
    getEmoji(risk) {
        switch(risk) {
            case 'Risk-Free': return '🛡️';
            case 'Conservative': return '🌱';
            case 'Balanced': return '⚖️';
            case 'Aggressive': return '🚀';
            default: return '📊';
        }
    },
    tileBorderClass(risk) {
      return {
        "border-gray-100 hover:border-gray-300": risk === "Risk-Free",
        "border-emerald-100 hover:border-emerald-300": risk === "Conservative",
        "border-blue-100 hover:border-blue-300": risk === "Balanced",
        "border-indigo-100 hover:border-indigo-300": risk === "Aggressive",
      };
    },
    getReturnColorBadge(pct) {
      if (pct > 0) return "bg-emerald-50 text-emerald-700";
      if (pct < 0) return "bg-rose-50 text-rose-700";
      return "bg-gray-100 text-gray-600";
    },
    formatNumber(value) {
      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    handleInput(event) {
      const val = event.target.value.replace(/[^0-9]/g, "");
      this.rawValue = val ? parseInt(val, 10) : 0;
    },
    async fetchMultipliers() {
      this.isLoading = true;
      try {
        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:5000";
        const res = await axios.get(`${apiUrl}/homepage_data`, {
          params: { country: this.selectedCountry }
        });
        this.multipliers = res.data;
      } catch (err) {
        console.error("❌ Failed to fetch homepage data:", err);
        // Fallback for demo
        this.multipliers = {
            "Risk-Free": 1.04, "Conservative": 1.06, "Balanced": 1.09, "Aggressive": 1.12
        };
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    selectedCountry(val) {
      try { localStorage.setItem('selectedCountry', val); } catch (e) { /* ignore */ }
      this.fetchMultipliers(); 
    }
  },
  async mounted() {
    try {
        // 1. Try local storage first
        const saved = localStorage.getItem('selectedCountry');
        if (saved) {
            this.selectedCountry = saved;
        } else {
            // 2. Try detection
            const detected = await detectUserCountry();
            if (detected) this.selectedCountry = detected;
        }
    } catch(e) { console.log(e) }
    this.fetchMultipliers();
  },
};
</script>